"use client"

import {
  Cell,
  ColumnDef,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  OnChangeFn,
  PaginationOptions,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import { useCallback, useEffect, useState } from "react"
import { debounce } from "lodash"
import { Box, Card, Skeleton, Table, TableContainer, Tbody, Td, Tooltip, Tr } from "@chakra-ui/react";
import SearchField from "../SearchField";
import ColumnSidebar, { ColumnSidebarProps } from "./ColumnSidebar";
import TablePagination, { TablePaginationProps } from "./TablePagination";
import TableHeader, { TableHeaderProps } from "./TableHeader";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

interface CustomDataTableProps<TData> extends Pick<ColumnSidebarProps<TData>, "triggerButtonType" | "columnsSidebarChildren" | 'onSavePreferences'> {
  search?: string,
  onPageChange?: (pagination: PaginationState) => void,
  onSearch?: (search: string) => void,
  isLoading?: boolean,
  conttroller?: React.ReactNode
  onSort?: (sorting: SortingState) => void
  manualSorting?: boolean;
  sort?: SortingState;
  onUpdateData?: (rowIndex: number, columnId: string, value: any, item?: Row<any>) => void;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>
  showColumns?: boolean;
  showPagination?: boolean;
  columnTotal?: {
    data: { title: string | undefined, value: string[] | string | number, colspan?: number}[]
  };
  columnPositions?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>
  columnPinning?: ColumnPinningState;
  enableMultiRowSelection?: boolean | ((row: Row<TData>) => boolean);
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
}

interface DataTableProps<TData, TValue> extends CustomDataTableProps<TData>, 
  PaginationOptions, 
  PaginationState, 
  Pick<TablePaginationProps<TData>, 'showTotal' | 'label'>,
  Pick<TableHeaderProps<TData>, 'disableAllDrag'> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[],
}

function DataTable<TData, TValue = unknown>({
  columns,
  data,
  autoResetPageIndex,
  getPaginationRowModel,
  manualPagination = true,
  manualSorting = true,
  pageCount,
  rowCount,
  pageSize,
  onPageChange,
  onSort,
  pageIndex,
  onSearch,
  search,
  isLoading,
  conttroller,
  onUpdateData,
  columnVisibility,
  onColumnVisibilityChange,
  columnSizing,
  onColumnSizingChange,
  sort,
  showColumns = false,
  columnsSidebarChildren,
  triggerButtonType,
  showPagination = true,
  columnTotal,
  onSavePreferences,
  onColumnOrderChange,
  columnPositions,
  showTotal,
  label = 'items',
  columnPinning,
  disableAllDrag = false, // New prop to disable all drag functionality
  enableRowSelection = false,
  enableMultiRowSelection = false,
  rowSelection: selectedRowSelection = {},
  onRowSelectionChange,
  getRowId
}: DataTableProps<TData, TValue>) {

  // ...existing state...
  const [columnOrder, setColumnOrder] = useState(
    columnPositions ?? columns.map(col => (col.id || (typeof col.id === "string" ? col.id : "")))
  );
  
  useEffect(() => {
    const validColumnIds = columns.map(col => col.id!);

    setColumnOrder((prev) => {
      // remove invalid columns
      const filtered = prev.filter(id => validColumnIds.includes(id));

      // add missing columns (new ones)
      const missing = validColumnIds.filter(id => !filtered.includes(id));

      return [...filtered, ...missing];
    });
  }, [columns]);
  
  const [sorting, setSorting] = useState<SortingState>(sort || []);
  
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex,
    pageSize,
  });

  const [defaultColumnVisibility, setDefaultColumnVisibility] = useState<VisibilityState>(columnVisibility || {});

  const [defaultColumnSizing, setDefaultColumnSizing] = useState<ColumnSizingState>(columnSizing || {});

  // RowSelectionState
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(selectedRowSelection);
  
  useEffect(() => {
        
    setRowSelection((prevState) => {
      return {
        ...prevState,
        ...selectedRowSelection
      };
    });
      
  }, [selectedRowSelection]);
  
  useEffect(() => {
    setDefaultColumnVisibility((prev) => {
      const next = columnVisibility || {};
      return prev === next ? prev : next;
    });
  }, [columnVisibility]);

  useEffect(() => {
    setDefaultColumnSizing((prev) => {
      const next = columnSizing || {};
      return prev === next ? prev : next;
    });
  }, [columnSizing]);

  useEffect(() => {
    setSorting((prev) => {
      const next = sort || [];
      return prev === next ? prev : next;
    });
  }, [sort]);

  useEffect(() => {
    setPagination((prev) => {
      if (prev.pageIndex === pageIndex && prev.pageSize === pageSize) return prev;
      return { pageIndex, pageSize };
    });
  }, [pageIndex, pageSize]);

  useEffect(() => {
    if (!columnPositions) return;

    const validColumnIds = columns.map(col => col.id!);

    const filtered = columnPositions.filter(id =>
      validColumnIds.includes(id)
    );

    const missing = validColumnIds.filter(id =>
      !filtered.includes(id)
    );

    setColumnOrder([...filtered, ...missing]);
  }, [columnPositions, columns]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = columnOrder.indexOf(active.id as string);
    const newIndex = columnOrder.indexOf(over.id as string);

    // 🚨 guard against invalid indexes
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(columnOrder, oldIndex, newIndex);

    setColumnOrder(newOrder);
    onColumnOrderChange?.(newOrder);

  }, [columnOrder, onColumnOrderChange]);
  
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
      columnVisibility: defaultColumnVisibility,
      columnSizing: defaultColumnSizing,
      columnOrder: columnOrder,
      columnPinning: columnPinning || { left: [], right: [] },
      rowSelection: rowSelection,
    },
    enableColumnPinning: true,
    onColumnOrderChange: onColumnOrderChange,
    getCoreRowModel: getCoreRowModel(),
    rowCount,
    manualPagination,
    manualSorting,
    autoResetPageIndex,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(next);
      onPageChange?.(next);
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
      onSort?.(next);
    },
    getPaginationRowModel,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(defaultColumnVisibility) : updater;
      setDefaultColumnVisibility(next);
      onColumnVisibilityChange?.(next);
    },
    onColumnSizingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(defaultColumnSizing) : updater;
      setDefaultColumnSizing(next);
      onColumnSizingChange?.(next);
    },
    onRowSelectionChange: (rowSelectionState) => {
      if (onRowSelectionChange) {
        onRowSelectionChange(rowSelectionState);
      }
      setRowSelection(rowSelectionState);
    },
    columnResizeMode: 'onChange',
    pageCount,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    enableRowSelection: enableRowSelection,
    enableMultiRowSelection: enableMultiRowSelection,
    meta: {
        updateData: onUpdateData
    },
    getRowId: getRowId
  });

  const debouncedSearch = debounce((value: string) => {
    if (onSearch) {
      onSearch(value);
    }
  }, 500);
  
  const getBodyColumnCell = (cell: Cell<TData, unknown>) => {

    const isNumeric = cell.column.columnDef.meta?.type === 'number' || 
                      cell.column.columnDef.meta?.type === 'currency' ? true : false;

    const content = flexRender(cell.column.columnDef.cell, cell.getContext());

    const isPinnedLeft = cell.column.getIsPinned?.() === 'left';
    const isPinnedRight = cell.column.getIsPinned?.() === 'right';
    
    let leftOffset = undefined;
    if (isPinnedLeft) {
      const cells = cell.row.getVisibleCells();
      const idx = cells.findIndex(c => c.column.id === cell.column.id);
      leftOffset = cells
        .slice(0, idx)
        .filter(c => c.column.getIsPinned?.() === 'left')
        .reduce((acc, c) => acc + (c.column.getSize?.() || 0), 0);
    }

    // Calculate right offset for pinned right columns
    let rightOffset = undefined;
    if (isPinnedRight) {
      const cells = cell.row.getVisibleCells();
      const idx = cells.findIndex(c => c.column.id === cell.column.id);
      rightOffset = cells
        .slice(idx + 1)
        .filter(c => c.column.getIsPinned?.() === 'right')
        .reduce((acc, c) => acc + (c.column.getSize?.() || 0), 0);
    }
    // const contextText = cell.getContext().getValue() as string;

    // const showTooltip = cell.column.columnDef.meta?.showTooltip === false;

    // const tempHeader = cell.column.columnDef.header?.toString();

    const hasCustomRender = cell.column.columnDef.meta?.customRender;

    // const getTooltipContent = () => {
    //   return (
    //     <Box
    //       padding="10px"
    //       display="flex"
    //       flexDirection="column"
    //       gap="5px"
    //     >
    //       <Box
    //         fontWeight={600}
    //       >
    //         {tempHeader}:
    //       </Box>
    //       <Box>{content}</Box>
    //     </Box>
    //   );
    // };
    
    if (hasCustomRender) {
      return (
        <Td 
          key={cell.id}
          textAlign={cell.column.columnDef.meta?.type === 'boolean' ? 'center' : 'left'}
          isNumeric={isNumeric}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          paddingTop="5px"
          paddingBottom="5px"
          style={{
            position: isPinnedLeft || isPinnedRight ? 'sticky' : undefined,
            left: isPinnedLeft ? leftOffset : undefined,
            right: isPinnedRight ? rightOffset : undefined,
            zIndex: isPinnedLeft || isPinnedRight ? 11 : undefined,
            backgroundColor: isPinnedLeft || isPinnedRight ? '#fff' : undefined,
            ...cell.column.columnDef.meta?.cellStyle
          }}
        >
          {hasCustomRender(cell.row, cell.column)}
        </Td>
      );
    }

    return (
      <Td 
        key={cell.id}
        textAlign={cell.column.columnDef.meta?.type === 'boolean' ? 'center' : 'left'}
        isNumeric={isNumeric}
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
        paddingTop="5px"
        paddingBottom="5px"
        style={{
          position: isPinnedLeft || isPinnedRight ? 'sticky' : undefined,
          left: isPinnedLeft ? leftOffset : undefined,
          right: isPinnedRight ? rightOffset : undefined,
          zIndex: isPinnedLeft || isPinnedRight ? 11 : undefined,
          backgroundColor: isPinnedLeft || isPinnedRight ? '#fff' : undefined,
          ...cell.column.columnDef.meta?.cellStyle
        }}
      >
        {content}
        {/* {!showTooltip ? (
          <Tooltip 
            hasArrow={true}
            label={getTooltipContent()}
            placement="bottom"
            backgroundColor="#fff"
            color="#000"
            padding="10px"
            closeOnPointerDown={false}
            closeOnEsc={false}
          >
            <span>
              {content}
            </span>
          </Tooltip>
        ) : content} */}
      </Td>
    );
  };

  // Calculate total columns
  const calculateTotalColumns = (columns: any): number => {
    let totalColumns = 0;
  
    columns.forEach((column: any) => {

      if (column.columns && column.columns.length > 0) {
        totalColumns += calculateTotalColumns(column.columns as any);
      } else {
        totalColumns += 1;
      }
    });
  
    return totalColumns;

  };
    
  const intializedTable = () => {

    const totalColumns = calculateTotalColumns(columns);

    if (isLoading) {
      return Array.from({ length: pageSize }, (_, index) => (
        <Tr key={index}>
            {Array.from({ length: totalColumns }, (_, cellIndex) => (
            <Td 
              key={cellIndex}
              paddingTop="5px"
              paddingBottom="5px"
            >
              <Skeleton
                height={3}
                margin="5px 0"
              />
            </Td>
          ))}
        </Tr>
      ));
    } else {
      return (
        <>
          {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  fontSize="14px"
                >
                  {row.getVisibleCells().map((cell) => getBodyColumnCell(cell))}
                </Tr>
              ))
            ) : (
              <Tr>
                <Td 
                  colSpan={columns.length} 
                  textAlign="center"
                  paddingTop="5px"
                  paddingBottom="5px"
                >
                  No results.
                </Td>
              </Tr>
            )}
            {columnTotal && (
              <>
                {columnTotal.data.map((totalColumn, index) => (
                  <Tr
                    key={index}
                    fontWeight={600}
                    borderTop={index === 0 ? '2px solid #d0d5dd' : 'none'}
                  >
                    {totalColumn.title && (
                      <Td 
                        colSpan={totalColumn.colspan ?? table.getAllFlatColumns().length - (Array.isArray(totalColumn.value) ? totalColumn.value.length : 1)}
                        paddingTop="5px"
                        paddingBottom="5px"
                      >
                        {totalColumn.title}
                      </Td>
                    )}
                      {Array.isArray(totalColumn.value) ? totalColumn.value.map((innerValue, index) => (
                        <Td 
                          key={index}
                          paddingTop="5px"
                          paddingBottom="5px"
                        >
                          {innerValue}
                        </Td>
                      )) : (
                        <Td
                          paddingTop="5px"
                          paddingBottom="5px"
                        >
                          {totalColumn.value}
                        </Td>
                      )}
                  </Tr>
                ))}
              </>
            )}
        </>
      );
    }
  };

  return (
    <Box 
      display="flex"
      flexDirection="column"
      gap="10px"
      flex={1}
    >
      <Box 
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexFlow="row wrap"
        gap="10px"
        className="data-table-header"
      >
        {onSearch && (
          <SearchField 
            value={search}
            onChange={(e) => debouncedSearch(e.target.value)}
          />
        )}
        {showColumns && (
          <ColumnSidebar<TData>
            table={table}
            columnsSidebarChildren={columnsSidebarChildren}
            triggerButtonType={triggerButtonType}
            onSavePreferences={onSavePreferences}
          />
        )}
        {conttroller}
      </Box>
      <Box 
        display="flex"
        gap="10px"
        flex={1}
      >
        <Card
          flex={1}
          position="relative"
          minHeight="570px"
        >
          <TableContainer
            overflowX="auto"
            overflowY="auto"
            position="absolute"
            top="0"
            right="0"
            bottom="0"
            left="0"
          >
            <DndContext onDragEnd={handleDragEnd}>
              <Table 
                variant="simple"
                style={{
                  tableLayout: 'fixed',
                  // width: '100%'
                }}
              >
                {/* header */}
                <TableHeader<TData> 
                  table={table}
                  onDragEnd={handleDragEnd}
                  disableAllDrag={disableAllDrag}
                />
                {/* body */}
                <Tbody>
                  {intializedTable()}
                </Tbody>
              </Table>
            </DndContext>
          </TableContainer>
        </Card>
      </Box>
      {showPagination && (
        <TablePagination<TData> 
          table={table}
          rowCount={rowCount}
          showTotal={showTotal}
          label={label}
        />
      )}
    </Box>
  )
}

export default DataTable